import $ from "jquery";

/**
 * Archive filter URL format: canonical is prefix-less (`?industry=x`).
 * Legacy `filter_` prefix (`?filter_industry=x`) accepted on read only,
 * for backward compat with pre-unification menu/marketing links.
 * URL builders must emit canonical format only.
 */
export const LEGACY_FILTER_PREFIX = "filter_";

/** Strip legacy `filter_` prefix from a URL param key. */
export function stripLegacyFilterPrefix(key) {
	return key.startsWith(LEGACY_FILTER_PREFIX)
		? key.slice(LEGACY_FILTER_PREFIX.length)
		: key;
}

/** Read URL param by canonical name, falling back to legacy prefixed form. */
export function getUrlParamWithLegacy(urlParams, canonicalKey) {
	return urlParams.get(canonicalKey) ?? urlParams.get(LEGACY_FILTER_PREFIX + canonicalKey);
}

/**
 * Escape HTML special characters to prevent XSS.
 *
 * @param {string} str
 * @returns {string}
 */
export function escapeHTML(str) {
	if (typeof str !== "string") return String(str);
	return str
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#039;");
}

/**
 * Build pagination HTML using UIkit classes.
 *
 * @param {number} currentPage
 * @param {number} totalPages
 * @param {Object} [options]
 * @param {string} [options.linkClass="pagination-link"]  - CSS class for clickable links
 * @param {boolean} [options.useArrows=true]               - Use UIkit arrow icons (false = text labels)
 * @param {string} [options.prevText="PREV"]               - Text for previous button when useArrows=false
 * @param {string} [options.nextText="NEXT"]               - Text for next button when useArrows=false
 * @param {number} [options.range=2]                       - Pages to show around current page
 * @param {Function} [options.getPageUrl]                  - If provided, uses href URLs instead of data-page
 * @returns {string} HTML string
 */
export function buildPaginationHTML(currentPage, totalPages, options = {}) {
	const {
		linkClass = "pagination-link",
		useArrows = true,
		prevText = "PREV",
		nextText = "NEXT",
		range = 2,
		getPageUrl = null,
	} = options;

	if (totalPages <= 1) return "";

	const makeLink = (page, content) => {
		if (getPageUrl) {
			return `<a class="${linkClass}" href="${getPageUrl(page)}">${content}</a>`;
		}
		return `<a class="${linkClass}" data-page="${page}" href="#">${content}</a>`;
	};

	let html = '<ul class="uk-pagination uk-flex-center">';

	// Previous
	if (currentPage > 1) {
		const prevContent = useArrows ? '<span uk-pagination-previous></span>' : prevText;
		html += `<li>${makeLink(currentPage - 1, prevContent)}</li>`;
	}

	// Page range (range=2 shows up to 4 visible page numbers)
	const windowSize = range * 2 - 1;
	let startPage = Math.max(1, currentPage - range);
	let endPage = Math.min(totalPages, startPage + windowSize);
	if (endPage - startPage < windowSize && startPage > 1) {
		startPage = Math.max(1, endPage - windowSize);
	}

	// First page + ellipsis
	if (startPage > 1) {
		html += `<li>${makeLink(1, "1")}</li>`;
		if (startPage > 2) {
			html += '<li class="uk-disabled"><span>...</span></li>';
		}
	}

	// Page numbers
	for (let i = startPage; i <= endPage; i++) {
		if (i === currentPage) {
			html += `<li class="uk-active"><span>${i}</span></li>`;
		} else {
			html += `<li>${makeLink(i, String(i))}</li>`;
		}
	}

	// Last page + ellipsis
	if (endPage < totalPages) {
		if (endPage < totalPages - 1) {
			html += '<li class="uk-disabled"><span>...</span></li>';
		}
		html += `<li>${makeLink(totalPages, String(totalPages))}</li>`;
	}

	// Next
	if (currentPage < totalPages) {
		const nextContent = useArrows ? '<span uk-pagination-next></span>' : nextText;
		html += `<li>${makeLink(currentPage + 1, nextContent)}</li>`;
	}

	html += "</ul>";
	return html;
}

/**
 * Update browser URL with filter state using pushState.
 *
 * @param {Object} filters           - Active filters {key: [values]}
 * @param {number} page              - Current page number
 * @param {Object} [options]
 * @param {string} [options.basePath]              - Base path (defaults to window.location.pathname)
 * @param {string} [options.paramPrefix="filter_"] - Prefix for filter params ("" for no prefix)
 * @param {string} [options.pageParam="paged"]     - Page parameter name
 * @param {string} [options.searchQuery=""]        - Search query string
 * @param {string} [options.searchParam="s"]       - Search parameter name
 * @param {string[]} [options.skipFilters=[]]      - Filter keys to exclude from URL
 * @param {boolean} [options.storeState=true]      - Store filters/page in history state object
 */
export function updateURLParameters(filters, page, options = {}) {
	const {
		basePath = window.location.pathname,
		paramPrefix = "filter_",
		pageParam = "paged",
		searchQuery = "",
		searchParam = "s",
		skipFilters = [],
		storeState = true,
	} = options;

	const parts = [];

	if (searchQuery) {
		parts.push(encodeURIComponent(searchParam) + "=" + encodeURIComponent(searchQuery));
	}

	Object.entries(filters).forEach(([key, values]) => {
		if (skipFilters.includes(key)) return;
		if (!Array.isArray(values) || values.length === 0) return;
		// Join with plain comma (not %2C) to match existing site URLs
		parts.push(encodeURIComponent(paramPrefix + key) + "=" + [...new Set(values)].map(encodeURIComponent).join(","));
	});

	if (page > 1) {
		parts.push(encodeURIComponent(pageParam) + "=" + page);
	}

	const queryString = parts.join("&");
	const newURL = basePath + (queryString ? "?" + queryString : "");

	const state = storeState ? { filters, page } : {};
	window.history.pushState(state, "", newURL);
}

/**
 * Parse filter values from URL query parameters.
 *
 * @param {Object} [options]
 * @param {string} [options.paramPrefix="filter_"] - Prefix to strip from param names
 * @param {string} [options.pageParam="paged"]     - Page parameter name
 * @returns {{ filters: Object, page: number }}
 */
export function parseURLFilters(options = {}) {
	const {
		paramPrefix = "filter_",
		pageParam = "paged",
	} = options;

	const params = new URLSearchParams(window.location.search);
	const filters = {};
	const page = parseInt(params.get(pageParam) || "1", 10);

	params.forEach((value, key) => {
		if (paramPrefix && key.startsWith(paramPrefix)) {
			const filterName = key.slice(paramPrefix.length);
			const values = value.split(",").map((v) => v.trim()).filter(Boolean);
			if (values.length) {
				filters[filterName] = values;
			}
		} else if (!paramPrefix && key !== pageParam) {
			const values = value.split(",").map((v) => v.trim()).filter(Boolean);
			if (values.length) {
				filters[key] = values;
			}
		}
	});

	return { filters, page };
}

/**
 * Restore checkbox states from parsed filter data.
 *
 * @param {jQuery} $wrapper         - Container element to search within
 * @param {Object} filters          - Parsed filters {key: [values]}
 * @param {Object} [options]
 * @param {Function} [options.onCheck] - Callback(filterKey, value) when a checkbox is checked
 */
export function restoreCheckboxesFromFilters($wrapper, filters, options = {}) {
	const { onCheck } = options;

	Object.entries(filters).forEach(([filterKey, values]) => {
		if (!Array.isArray(values)) return;
		values.forEach((val) => {
			$wrapper
				.find(`input[name="filters[${filterKey}][]"][value="${val}"]`)
				.each(function () {
					$(this).prop("checked", true);
					$(this)
						.closest("li.ais-RefinementList-item")
						.addClass("ais-RefinementList-item--selected");
					if (onCheck) onCheck(filterKey, val);
				});
		});
	});
}

/**
 * Build refinement tags HTML showing active filters.
 *
 * @param {Object} filters              - Active filters {key: [values]}
 * @param {Function} getLabel           - (filterKey, value) => display label
 * @param {Object} [options]
 * @param {string} [options.clearText="Clear filters"] - Clear button text
 * @param {string[]} [options.skipFilters=[]]           - Filter keys to skip
 * @returns {string} HTML string (empty string if no active filters)
 */
export function buildRefinementTagsHTML(filters, getLabel, options = {}) {
	const {
		clearText = "Clear filters",
		skipFilters = [],
	} = options;

	const activeEntries = Object.entries(filters).filter(
		([key, values]) => !skipFilters.includes(key) && Array.isArray(values) && values.length > 0
	);

	if (activeEntries.length === 0) return "";

	let html = '<div class="uk-grid uk-grid-xsmall uk-flex-middle" uk-grid>';

	activeEntries.forEach(([filterKey, values]) => {
		values.forEach((value) => {
			const label = escapeHTML(getLabel(filterKey, value));
			const safeKey = escapeHTML(filterKey);
			const safeValue = escapeHTML(value);
			html +=
				'<div><button class="filter-tag uk-button uk-button-indigo-outline uk-flex uk-flex-middle" data-filter="' +
				safeKey +
				'" data-value="' +
				safeValue +
				'">' +
				label +
				'<span class="uk-margin-xsmall-left uk-icon" uk-icon="icon: close"></span></button></div>';
		});
	});

	html +=
		'<div><a href="#" class="uk-button uk-link-indigo uk-button-link ais-ClearRefinements-button">' +
		escapeHTML(clearText) +
		"</a></div>";
	html += "</div>";

	return html;
}

/**
 * Get display label for a filter value from a checkbox in the DOM.
 *
 * @param {jQuery} $wrapper    - Container to search within
 * @param {string} filterKey   - Filter name
 * @param {string} value       - Filter value
 * @returns {string} Label text or the value itself as fallback
 */
export function getFilterLabelFromDOM($wrapper, filterKey, value) {
	// Try array-style name first (checkboxes), then scalar name (radio-style)
	let $input = $wrapper
		.find(`input[name="filters[${filterKey}][]"][value="${value}"]`)
		.first();
	if (!$input.length) {
		$input = $wrapper
			.find(`input[name="filters[${filterKey}]"][value="${value}"]`)
			.first();
	}
	if ($input.length) {
		const label = $input.closest("label").find("span.uk-width-expand").text().trim();
		if (label) return label;
	}
	return value;
}

/**
 * Sync paired checkbox (sidebar ↔ mobile) checked state.
 *
 * @param {jQuery} $wrapper    - Container to search within
 * @param {string} filterKey   - Filter name
 * @param {string} value       - Filter value
 * @param {boolean} isChecked  - Whether to check or uncheck
 */
export function syncPairedCheckbox($wrapper, filterKey, value, isChecked) {
	$wrapper.find(`input[name="filters[${filterKey}][]"][value="${value}"]`).each(function () {
		$(this).prop("checked", isChecked);
		if (isChecked) {
			$(this).closest("li.ais-RefinementList-item").addClass("ais-RefinementList-item--selected");
		} else {
			$(this).closest("li.ais-RefinementList-item").removeClass("ais-RefinementList-item--selected");
		}
	});
}
